import { z } from 'zod';
import { CARD_MAX_LEVEL } from '../inventory/cards';
import { itemDefinitionIdSchema } from '../inventory/definitions';
import { cardSlotSchema } from '../inventory/loadout';

/**
 * **Fundir duas cartas iguais** (gate V28H.C).
 *
 * ## Porque um contrato próprio, e não o dos artefactos
 *
 * Porque a resposta é outra. `artifactCombineRequestSchema` responde com uma
 * sessão de caça reconfigurada, uma instantânea, um digest e o que a cauda
 * pendente rendeu - tudo isso porque um artefacto mais forte muda a corrida em
 * curso. Uma carta ainda não entra em corrida nenhuma: não tem espaço, não tem
 * cooldown em simulação e não tem habilidade. Reutilizar aquele contrato
 * obrigaria esta rota a inventar uma sessão para preencher campos.
 *
 * O que este contrato tem em comum com aquele é a única coisa que importa: o
 * pedido nomeia duas instâncias e nada mais.
 *
 * ## A fusão é MANUAL, e vai continuar a ser
 *
 * Nada funde duplicados por conta do jogador. Uma segunda cópia é uma coisa que
 * ele pode querer guardar para outro espaço, e consumi-la automaticamente seria
 * gastar um item que ninguém ofereceu.
 */

export const CARD_COMBINE_ROUTE = '/api/cards/combine' as const;

/**
 * Duas instâncias e uma guarda de repetição. É o pedido inteiro.
 *
 * Não pode nomear uma definição, um nível nem um resultado: os três são lidos
 * das duas linhas, sob os seus bloqueios, dentro da transação. Um cliente que
 * mentisse estaria apenas a nomear itens que não tem.
 *
 * `.strict()`, para que um pedido que tente acrescentar `level`, `definitionId`
 * ou `toLevel` seja RECUSADO em vez de ignorado - um campo ignorado é um cliente
 * a acreditar que pediu uma coisa que o servidor nunca leu.
 */
export const cardCombineRequestSchema = z
  .object({
    firstInstanceId: z.uuid(),
    secondInstanceId: z.uuid(),
    /** Guarda de repetição para ESTE pedido. Nunca uma chave de ledger. */
    idempotencyKey: z.string().min(8).max(128),
  })
  .strict();
export type CardCombineRequest = z.infer<typeof cardCombineRequestSchema>;

export const cardCombineResponseSchema = z
  .object({
    /** O que a fusão fez, a partir do registo do próprio servidor. */
    combined: z
      .object({
        definitionId: itemDefinitionIdSchema,
        consumedInstanceIds: z.tuple([z.uuid(), z.uuid()]),
        newInstanceId: z.uuid(),
        fromLevel: z
          .number()
          .int()
          .min(1)
          .max(CARD_MAX_LEVEL - 1),
        toLevel: z.number().int().min(2).max(CARD_MAX_LEVEL),
      })
      .strict(),
    /** Verdadeiro quando a mesma chave já tinha sido aplicada; nada fundiu de novo. */
    replayed: z.boolean(),
  })
  .strict();
export type CardCombineResponse = z.infer<typeof cardCombineResponseSchema>;

/* -------------------------------------------------------------------------- */
/*  Os três espaços (gate V28H.D)                                             */
/* -------------------------------------------------------------------------- */

/**
 * **Ler e mexer nos três espaços de carta.**
 *
 * ## Porque rotas próprias, e não `LOADOUT_EQUIP_ROUTE`
 *
 * A instrução deste gate é clara: ampliar a operação genérica de loadout se ela
 * já exprimir espaço, instância, versão esperada e idempotência. Ela não
 * exprime **espaço**. `loadoutEquipRequestSchema` carrega uma instância e uma
 * versão, e o servidor DEDUZ o espaço da categoria - o que funciona quando há
 * um espaço por categoria e falha aqui, onde três espaços aceitam a mesma
 * categoria e nada no item diz qual deles o jogador quer.
 *
 * E a resposta dela é a segunda razão: ela devolve sessão, instantânea, digest
 * e o que a cauda pendente rendeu, porque trocar de nave RECONFIGURA a corrida.
 * Uma carta não reconfigura nada neste gate. Passar por ali obrigaria esta
 * operação a liquidar uma janela de caça para preencher campos que a carta não
 * mexe - que é precisamente o que o contrato deste gate proíbe.
 *
 * É o mesmo raciocínio que deu rotas próprias ao equipamento
 * (`/api/equipment/equip`) e à skin: mesma FORMA, mesmas guardas, resposta que
 * diz a verdade sobre o que mudou.
 */
export const CARDS_ROUTE = '/api/cards' as const;
export const CARD_EQUIP_ROUTE = '/api/cards/equip' as const;
export const CARD_UNEQUIP_ROUTE = '/api/cards/unequip' as const;

/**
 * Um espaço, e o que está nele.
 *
 * `card` é nulo num espaço vazio, e um espaço vazio é o estado normal: uma
 * conta equipa de zero a três.
 */
export const cardSlotViewSchema = z
  .object({
    slot: cardSlotSchema,
    card: z
      .object({
        instanceId: z.uuid(),
        definitionId: itemDefinitionIdSchema,
        level: z.number().int().min(1).max(CARD_MAX_LEVEL),
      })
      .strict()
      .nullable(),
  })
  .strict();
export type CardSlotView = z.infer<typeof cardSlotViewSchema>;

/**
 * O estado dos três espaços.
 *
 * Sempre TRÊS entradas, pela ordem de `CARD_SLOTS`, e nunca uma lista curta
 * porque um espaço está vazio: a interface desenha três caixas, e uma lista que
 * encolhe obrigá-la-ia a adivinhar quais faltam.
 *
 * `loadoutVersion` é a versão que o próximo pedido tem de citar. É a mesma
 * versão de todos os outros espaços do loadout - equipar uma carta e trocar de
 * nave disputam o mesmo número, de propósito: as duas escrevem a mesma linha.
 */
export const cardsStateSchema = z
  .object({
    slots: z.array(cardSlotViewSchema).length(3),
    loadoutVersion: z.number().int().min(0),
  })
  .strict();
export type CardsState = z.infer<typeof cardsStateSchema>;
export const cardsListResponseSchema = cardsStateSchema;
export type CardsListResponse = z.infer<typeof cardsListResponseSchema>;

/**
 * Pôr uma carta num espaço.
 *
 * O cliente nomeia o espaço, a instância, a versão que julga ter e uma chave.
 * **Nunca** a definição, o nível, a categoria, a posse ou o estado da partida:
 * não há campo onde caibam, e os cinco são relidos sob os bloqueios, dentro da
 * transação.
 *
 * `.strict()`, para que um pedido que tente acrescentar `definitionId` ou
 * `level` seja recusado em vez de ignorado.
 */
export const cardEquipRequestSchema = z
  .object({
    slot: cardSlotSchema,
    itemInstanceId: z.uuid(),
    expectedLoadoutVersion: z.number().int().min(0),
    idempotencyKey: z.string().min(8).max(128),
  })
  .strict();
export type CardEquipRequest = z.infer<typeof cardEquipRequestSchema>;

/** Esvaziar um espaço. O espaço, a versão e a chave. */
export const cardUnequipRequestSchema = z
  .object({
    slot: cardSlotSchema,
    expectedLoadoutVersion: z.number().int().min(0),
    idempotencyKey: z.string().min(8).max(128),
  })
  .strict();
export type CardUnequipRequest = z.infer<typeof cardUnequipRequestSchema>;

/**
 * O que mudou, mais o estado depois de mudar.
 *
 * ## O que ela deliberadamente NÃO carrega, e a interface tem de o dizer
 *
 * Sessão, instantânea, digest e liquidação. Uma caçada a correr **não é
 * tocada** por esta operação: a simulação não é reconfigurada, a instantânea
 * não muda, nenhuma carta é injectada na sessão em curso e nada reinicia.
 *
 * A escolha feita agora vale a partir do **próximo congelamento de loadout** -
 * a sessão de caça seguinte. Uma interface que sugerisse o contrário estaria a
 * prometer um efeito que o servidor não produz, e é por isso que isto está
 * escrito no contrato e não só numa nota de gate.
 */
export const cardMutationResponseSchema = cardsStateSchema
  .extend({
    changed: z
      .object({
        slot: cardSlotSchema,
        previousInstanceId: z.uuid().nullable(),
        newInstanceId: z.uuid().nullable(),
        previousVersion: z.number().int().min(0),
        newVersion: z.number().int().min(1),
      })
      .strict(),
    /** Verdadeiro quando a mesma chave já tinha sido aplicada; nada se mexeu. */
    replayed: z.boolean(),
  })
  .strict();
export type CardMutationResponse = z.infer<typeof cardMutationResponseSchema>;
